import { frontDeskAccessOf, type FrontDeskAccess, type MyBusiness } from "@teamna/shared";
import type { LabelConcept } from "@teamna/web-client/hooks/use-labels.ts";
import {
  CalendarIcon,
  CameraIcon,
  ClockIcon,
  HelpIcon,
  CardIcon,
  ChatIcon,
  HomeIcon,
  LayersIcon,
  PeopleIcon,
  PersonIcon,
  PlusCircleIcon,
  RepeatIcon,
  SettingsIcon,
  ShieldIcon,
  SparkIcon,
  TicketIcon,
  type ShellNavGroup,
  type ShellNavItem,
} from "../components/index.ts";

type Label = (concept: LabelConcept, opts?: { plural?: boolean; capital?: boolean }) => string;

export type AssistantNavState = "on" | "locked" | "hidden";

/**
 * The AI Assistant menu item of a business. The owner and admins always see it: with a lock while the
 * package is not on. The server sends `assistantPackage` only to these two roles. Control Center
 * acts as the owner, so it shows the same item.
 */
export function assistantNavState(business: MyBusiness | null | undefined): AssistantNavState {
  if (business?.aiAssistant === true) return "on";
  return business?.assistantPackage ? "locked" : "hidden";
}

/**
 * The owner office menu (owner menu revamp, 2026-10-09; prototype `teamna/prototypes/owner-menu.html`).
 *
 * The owner runs three things, and each one is a group: what the business sells (the schedule,
 * the weekly timetable, the groups, the packages), its customers (the people who book, the people
 * who attend, the money they pay), and its team. Home and Inbox sit above the groups because they
 * touch all three. The order is by daily use: the schedule first, the team last. Tools (Team photo
 * and WhatsApp sync) come after the team (asked by Anas on 2026-10-09: the revamp left them
 * without a menu item).
 *
 * The words come from the business's labels, so a swimming academy reads "Parents and swimmers"
 * and "Levels". Where the people who book are the people who attend (a pilates
 * studio's "Clients"), there is one people page, not two with the same name.
 *
 * The shape decides what the people group has (F2). Where clients book for themselves
 * (`booking_for = self`) there is one page of clients, in the business's member word: no page of
 * participants, no board, and no "New participants" (names a coach typed, for the owner to link to
 * a parent).
 *
 * Desk and Control Center (act-as) draw the same menu from here.
 */
export function ownerNavGroups({
  label,
  unread,
  todo = 0,
  teamPhoto = true,
  assistant = "hidden",
  forChildren,
  frontDesk = false,
  frontDeskAccess,
}: {
  label: Label;
  /** Members book for their children (`useShape().forChildren`). False: clients book for themselves. */
  forChildren: boolean;
  /** Messages and activity the owner did not read yet: the Inbox badge. */
  unread: number;
  /** The jobs on the To do list of Home: the Home badge, so an owner on another page sees them. */
  todo?: number;
  /** False in Control Center, which has no Team photo page (it is off on the Team page there too). */
  teamPhoto?: boolean;
  /**
   * AI Assistant, after Home (ai-assistant-v1.md). `on`: the business bought the package or Teamna gave
   * it. `locked`: the item shows with a lock and opens the offer. `hidden` (the default): a person who
   * does not run the business. See `assistantNavState`.
   */
  assistant?: AssistantNavState;
  /** The Front desk role (T1): only the pages it may open (`frontDeskMayOpen`). */
  frontDesk?: boolean;
  /** The business's Front desk setting (`MyBusiness.frontDesk`): the pages the owner opened to it. Missing: the start values. */
  frontDeskAccess?: Partial<FrontDeskAccess> | null;
}): ShellNavGroup[] {
  const members = label("member", { plural: true, capital: true });
  const twoWords = forChildren && label("member") !== label("participant");
  const people: ShellNavItem[] = [
    { to: "/owner/parents", label: members, icon: PeopleIcon },
    ...(twoWords
      ? [{ to: "/owner/participants", label: label("participant", { plural: true, capital: true }), icon: PersonIcon }]
      : []),
    // Names coaches typed, for the owner to link to a parent (coach-adds-swimmers, 2026-10-09).
    ...(forChildren ? [{ to: "/owner/new-participants", label: `New ${label("participant", { plural: true })}`, icon: PlusCircleIcon }] : []),
    { to: "/owner/payments", label: "Payments", icon: CardIcon },
  ];

  const groups: ShellNavGroup[] = [
    {
      items: [
        { to: "/owner/home", label: "Home", icon: HomeIcon, ...(todo > 0 ? { badge: todo } : {}) },
        ...(assistant !== "hidden" ? [{ to: "/owner/assistant", label: "AI Assistant", icon: SparkIcon, ...(assistant === "locked" ? { locked: true } : {}) }] : []),
        { to: "/owner/inbox", label: "Inbox", icon: ChatIcon, ...(unread > 0 ? { badge: unread } : {}) },
      ],
    },
    {
      label: "Classes and packages",
      items: [
        { to: "/owner/schedule", label: "Calendar", icon: CalendarIcon },
        { to: "/owner/groups", label: label("group", { plural: true, capital: true }), icon: LayersIcon },
        { to: "/owner/packages", label: "Packages and rules", icon: TicketIcon },
      ],
    },
    {
      label: twoWords ? `${members} and ${label("participant", { plural: true })}` : members,
      items: people,
    },
    {
      label: "Team",
      items: [
        { to: "/owner/team", label: "Staff", icon: ShieldIcon },
        // A monthly report, to pay the team per class (Pilates studio shape, T2).
        { to: "/owner/staff-classes", label: `Classes per ${label("staff")}`, icon: ClockIcon },
      ],
    },
    {
      label: "Tools",
      items: [
        ...(teamPhoto ? [{ to: "/owner/tools/team-photo", label: "Team photo", icon: CameraIcon }] : []),
        { to: "/owner/tools/whatsapp-sync", label: "WhatsApp sync", icon: RepeatIcon },
      ],
    },
  ];
  if (!frontDesk) return groups;
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => frontDeskMayOpen(item.to, frontDeskAccess)) }))
    .filter((group) => group.items.length > 0);
}

/**
 * The pages of the office that the Front desk role always opens (migration 0080, T1): Home with its
 * To do list (new clients to approve), Inbox, the Calendar (book a client, check people in), the
 * people pages (a client's profile, record a payment), and Help and support. Not Packages and rules,
 * the timetable, the tools, Settings, Get paid or Billing. The menu and the office's guard both read
 * this list and `FRONT_DESK_ITEM_PAGES`, and the API refuses the rest anyway
 * (`apps/desk-api/src/middleware/role.ts`).
 */
export const FRONT_DESK_PAGES = [
  "/owner/home",
  "/owner/inbox",
  "/owner/schedule",
  "/owner/parents",
  "/owner/participants",
  "/owner/support",
] as const;

/** The pages the owner opens to the front desk in Settings, Front desk, by item. */
export const FRONT_DESK_ITEM_PAGES: Partial<Record<keyof FrontDeskAccess, string>> = {
  classes: "/owner/groups",
  payments: "/owner/payments",
  team: "/owner/team",
};

/** Pages under `FRONT_DESK_PAGES` that change what the front desk may not change. */
const FRONT_DESK_CLOSED = ["/owner/inbox/announce", "/owner/participants/board"];

/** True when the front desk may open this office address, with the business's Front desk setting (start values when missing). */
export function frontDeskMayOpen(pathname: string, access?: Partial<FrontDeskAccess> | null): boolean {
  const inside = (page: string) => pathname === page || pathname.startsWith(`${page}/`);
  if (FRONT_DESK_CLOSED.some(inside)) return false;
  const on = frontDeskAccessOf(access);
  const opened = Object.entries(FRONT_DESK_ITEM_PAGES).filter(([item]) => on[item as keyof FrontDeskAccess]).map(([, page]) => page!);
  return [...FRONT_DESK_PAGES, ...opened].some(inside);
}

/**
 * Settings, at the bottom. Card payments (`/owner/setup/get-paid`) and the Teamna plan
 * (`/owner/billing`) open from the Settings page, so the item stays lit on them. Their addresses
 * do not move: Stripe sends the owner back to both.
 */
export function ownerSettingsItem(): ShellNavItem {
  return { to: "/owner/settings", label: "Settings", icon: SettingsIcon, also: ["/owner/setup/get-paid", "/owner/billing"] };
}

/**
 * Help and support (support-system.md, section 3), at the bottom above Settings. The badge counts
 * requests with a Teamna answer the person has not read. Not in Control Center: act-as cannot send
 * or read requests as the owner.
 */
export function ownerHelpItem(unread: number): ShellNavItem {
  return { to: "/owner/support", label: "Help and support", icon: HelpIcon, ...(unread > 0 ? { badge: unread } : {}) };
}
